import React, { useMemo } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { mergeSx } from 'merge-sx';

import { TestSessionSummaryDTO } from '@/api/generated';
import CameraIcon from '@/assets/icons/camera.svg?react';
import EvaluationChip from '@/components/EvaluationChip';
import TestSessionButtons from '@/components/TestSessionButtons';
import TestSessionProgressIcon from '@/components/TestSessionProgressIcon';
import { AI_ANALYSIS_STATUSES } from '@/containers/Process/options';
import { useFormattedDatetime } from '@/hooks/useFormattedDatetime';
import { isComplete, isRetest } from '@/utils/testSessions';

type TestSessionPreviewProps = {
  testSession: TestSessionSummaryDTO;
  expanded?: boolean;
};

const TestSessionPreview = ({ testSession }: TestSessionPreviewProps) => {
  const { palette, spacing } = useTheme();
  const { formatDatetime } = useFormattedDatetime();
  const isRetestOrComplete = isRetest(testSession) || isComplete(testSession);

  const { evaluation, isWebcamEnabled, aiAnalysisStatus, isAiAnalysisEnabled } = testSession;

  const aiStatusIcon = useMemo(() => {
    const iconComponent = AI_ANALYSIS_STATUSES.find(({ value }) => value === aiAnalysisStatus);

    return iconComponent?.Icon?.();
  }, [aiAnalysisStatus]);

  return (
    <Box
      sx={mergeSx(
        {
          padding: spacing(1),
          backgroundColor: palette.grey[100],
          borderRadius: spacing(0.5),
          ':hover': {
            backgroundColor: palette.grey[200]
          }
        },
        isRetestOrComplete && {
          backgroundColor: palette.primary[50],
          ':hover': { backgroundColor: palette.primary[100] }
        }
      )}
    >
      <Stack component="dt" sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" gap={spacing(1)} alignItems="center">
          <TestSessionProgressIcon testSession={testSession} />
          <Typography
            variant="body1"
            sx={{
              color: palette.text.primary,
              marginInlineStart: spacing(1)
            }}
          >
            {testSession.testDefinition?.name}
          </Typography>
          <EvaluationChip evaluation={evaluation} />
          {isWebcamEnabled && <CameraIcon />}
          {isAiAnalysisEnabled && aiAnalysisStatus && aiStatusIcon}
        </Stack>
        <TestSessionButtons testSession={testSession} />
      </Stack>
      {testSession.updatedAt && (
        <Typography component="dd" variant="body2" sx={{ color: 'grey.700', marginTop: spacing(1) }}>
          {formatDatetime(testSession.updatedAt)}
        </Typography>
      )}
    </Box>
  );
};

export default TestSessionPreview;
